'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useRouter } from 'next/router';
import PanelSessionLoader from '@/components/PanelSessionLoader';
import { applyUserPanelTheme } from '@/lib/panelTheme';
import { normalizeStoredAvatarUrl, avatarFallbackInitial } from '@/lib/avatarPublic';
import { agentLogout, getAgentSession } from '@/lib/agentSessionClient';
import { emitPanelLanguageSync } from '@/lib/panel-i18n/emitSync';
import {
  clearPanelSessionCache,
  hasPanelSessionCache,
  readPanelSessionCache,
  writePanelSessionCache,
} from '@/lib/panelSessionCache';

const AgentAuthContext = createContext(null);

function isAgentZone(pathname) {
  return typeof pathname === 'string' && pathname.startsWith('/agent');
}

function isAgentPublicPath(pathname) {
  if (pathname === '/agent' || pathname === '/agent/') return true;
  if (pathname === '/agent/signup') return true;
  if (pathname === '/agent/forgot-password') return true;
  return false;
}

function syncAgentPanelLanguage(session) {
  if (
    session?.ok &&
    session.preferredLanguage != null &&
    typeof session.preferredLanguage === 'string' &&
    session.preferredLanguage.trim()
  ) {
    emitPanelLanguageSync('agent', session.preferredLanguage);
  }
}

function agentProfileFromSession(username, email, name, avatarUrl) {
  const n = typeof name === 'string' ? name.trim() : '';
  const u = typeof username === 'string' ? username.trim() : '';
  const e = typeof email === 'string' ? email.trim() : '';
  const displayName = n || (u ? `@${u}` : 'Agent');
  const displayEmail = e || '—';
  const safeUrl = normalizeStoredAvatarUrl(typeof avatarUrl === 'string' ? avatarUrl : '') || '';
  const avatarInitial = avatarFallbackInitial(n, u, '?');
  return { displayName, displayEmail, avatarInitial, avatarUrl: safeUrl };
}

const IDLE_STATE = {
  status: 'idle',
  agentId: null,
  username: null,
  email: null,
  name: null,
  preferredTheme: 'dark',
  avatarUrl: '',
};

function guestState() {
  return {
    status: 'unauthenticated',
    agentId: null,
    username: null,
    email: null,
    name: null,
    preferredTheme: 'dark',
    avatarUrl: '',
  };
}

function authedFromSession(session) {
  return {
    status: 'authenticated',
    agentId: session.agentId,
    username: session.username,
    email: session.email,
    name: session.name ?? '',
    preferredTheme: session.preferredTheme === 'light' ? 'light' : 'dark',
    avatarUrl:
      normalizeStoredAvatarUrl(typeof session.avatarUrl === 'string' ? session.avatarUrl : '') || '',
  };
}

function sameAuthedSession(prev, next) {
  return (
    prev.status === 'authenticated' &&
    next.status === 'authenticated' &&
    prev.agentId === next.agentId &&
    prev.username === next.username &&
    prev.email === next.email &&
    prev.name === next.name &&
    prev.preferredTheme === next.preferredTheme &&
    prev.avatarUrl === next.avatarUrl
  );
}

function initialAgentState() {
  return readPanelSessionCache('agent') || IDLE_STATE;
}

export function AgentAuthProvider({ children }) {
  const router = useRouter();
  const pathname = router.pathname;
  const [state, setState] = useState(initialAgentState);
  const [avatarImageCacheKey, setAvatarImageCacheKey] = useState(0);
  const authenticatedRef = useRef(initialAgentState().status === 'authenticated');
  const stateRef = useRef(state);
  const routerRef = useRef(router);

  stateRef.current = state;
  routerRef.current = router;

  const bumpAvatarImageCache = useCallback(() => {
    setAvatarImageCacheKey((k) => k + 1);
  }, []);

  const commitState = useCallback((next) => {
    setState((prev) => {
      const resolved = typeof next === 'function' ? next(prev) : next;
      authenticatedRef.current = resolved.status === 'authenticated';
      stateRef.current = resolved;
      if (resolved.status === 'authenticated') {
        writePanelSessionCache('agent', resolved);
      } else if (resolved.status === 'unauthenticated') {
        clearPanelSessionCache('agent');
      }
      return resolved;
    });
  }, []);

  useEffect(() => {
    if (!isAgentZone(pathname) || state.status !== 'authenticated') return;
    applyUserPanelTheme(state.preferredTheme);
  }, [pathname, state.status, state.preferredTheme]);

  useEffect(() => {
    const cancelledRef = { current: false };

    (async () => {
      if (!router.isReady) return;

      if (!isAgentZone(pathname)) {
        if (stateRef.current.status !== 'idle') {
          stateRef.current = IDLE_STATE;
          setState(IDLE_STATE);
        }
        authenticatedRef.current = false;
        return;
      }

      const publicPath = isAgentPublicPath(pathname);

      const cached = readPanelSessionCache('agent');
      if (cached && stateRef.current.status !== 'authenticated') {
        authenticatedRef.current = true;
        stateRef.current = cached;
        setState(cached);
      }

      // Already in the panel — never re-gate or background-check mid-work.
      if (
        !publicPath &&
        (authenticatedRef.current || stateRef.current.status === 'authenticated')
      ) {
        return;
      }

      if (stateRef.current.status !== 'authenticated') {
        commitState((s) => (s.status === 'authenticated' ? s : { ...s, status: 'loading' }));
      }

      const session = await getAgentSession();
      if (cancelledRef.current) return;

      if (publicPath) {
        if (session.ok) {
          commitState(authedFromSession(session));
          syncAgentPanelLanguage(session);
          void routerRef.current.replace('/agent/dashboard');
        } else if (!session.networkError) {
          commitState(guestState());
        }
        return;
      }

      if (!session.ok) {
        if (session.networkError) return;
        commitState(guestState());
        if (session.status === 403) {
          void agentLogout();
        }
        void routerRef.current.replace('/agent');
        return;
      }

      commitState(authedFromSession(session));
      syncAgentPanelLanguage(session);
    })();

    return () => {
      cancelledRef.current = true;
    };
  }, [pathname, commitState, router.isReady]);

  const refresh = useCallback(async () => {
    if (!isAgentZone(pathname)) return;
    const publicPath = isAgentPublicPath(pathname);
    const session = await getAgentSession();
    if (session.networkError) return;
    if (publicPath) {
      if (session.ok) {
        commitState(authedFromSession(session));
        syncAgentPanelLanguage(session);
        void routerRef.current.replace('/agent/dashboard');
      } else {
        commitState(guestState());
      }
      return;
    }
    if (!session.ok) {
      commitState(guestState());
      void routerRef.current.replace('/agent');
      return;
    }
    const next = authedFromSession(session);
    if (!sameAuthedSession(stateRef.current, next)) {
      commitState(next);
    }
    syncAgentPanelLanguage(session);
  }, [pathname, commitState]);

  const applyAgentProfileToSession = useCallback(
    (profile) => {
      if (!profile || typeof profile !== 'object') return;
      commitState((s) => {
        if (s.status !== 'authenticated') return s;
        const next = { ...s };
        if (typeof profile.username === 'string') next.username = profile.username;
        if (typeof profile.email === 'string') next.email = profile.email;
        if (typeof profile.name === 'string') next.name = profile.name;
        if (profile.preferredTheme === 'light' || profile.preferredTheme === 'dark') {
          next.preferredTheme = profile.preferredTheme;
          applyUserPanelTheme(profile.preferredTheme);
        }
        if ('avatarUrl' in profile) {
          next.avatarUrl =
            typeof profile.avatarUrl === 'string'
              ? normalizeStoredAvatarUrl(profile.avatarUrl) || ''
              : '';
        }
        if (typeof profile.preferredLanguage === 'string' && profile.preferredLanguage.trim()) {
          emitPanelLanguageSync('agent', profile.preferredLanguage.trim());
        }
        return next;
      });
    },
    [commitState]
  );

  const persistPreferredTheme = useCallback(
    async (next) => {
      const t = next === 'light' ? 'light' : 'dark';
      applyUserPanelTheme(t);
      try {
        const res = await fetch('/api/agent/profile', {
          method: 'PATCH',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ preferredTheme: t }),
        });
        if (!res.ok) return;
        const data = await res.json().catch(() => ({}));
        if (data.profile && typeof data.profile === 'object') {
          applyAgentProfileToSession(data.profile);
        }
        const session = await getAgentSession();
        if (session.ok) {
          commitState(authedFromSession(session));
          syncAgentPanelLanguage(session);
        }
      } catch {
        /* DOM already updated */
      }
    },
    [applyAgentProfileToSession, commitState]
  );

  const signOut = useCallback(async () => {
    await agentLogout();
    commitState(guestState());
    applyUserPanelTheme('dark');
    await routerRef.current.push('/agent');
  }, [commitState]);

  const value = useMemo(() => {
    const { displayName, displayEmail, avatarInitial, avatarUrl } = agentProfileFromSession(
      state.username,
      state.email,
      state.name,
      state.avatarUrl
    );
    return {
      status: state.status,
      agentId: state.agentId,
      username: state.username,
      email: state.email,
      name: state.name,
      preferredTheme: state.preferredTheme === 'light' ? 'light' : 'dark',
      avatarUrl,
      avatarImageCacheKey,
      bumpAvatarImageCache,
      displayName,
      displayEmail,
      avatarInitial,
      isAuthenticated: state.status === 'authenticated',
      refresh,
      applyAgentProfileToSession,
      persistPreferredTheme,
      signOut,
    };
  }, [
    state.status,
    state.agentId,
    state.username,
    state.email,
    state.name,
    state.preferredTheme,
    state.avatarUrl,
    avatarImageCacheKey,
    bumpAvatarImageCache,
    refresh,
    applyAgentProfileToSession,
    persistPreferredTheme,
    signOut,
  ]);

  const inAgent = isAgentZone(pathname);
  const publicRoute = isAgentPublicPath(pathname);
  const protectedAgent = inAgent && !publicRoute;

  const keepPanel =
    state.status === 'authenticated' ||
    authenticatedRef.current ||
    hasPanelSessionCache('agent');

  if (protectedAgent && state.status !== 'authenticated' && !keepPanel) {
    return (
      <AgentAuthContext.Provider value={value}>
        <PanelSessionLoader variant="agent" />
      </AgentAuthContext.Provider>
    );
  }

  return <AgentAuthContext.Provider value={value}>{children}</AgentAuthContext.Provider>;
}

export function useAgentAuth() {
  const ctx = useContext(AgentAuthContext);
  if (!ctx) {
    throw new Error('useAgentAuth must be used within AgentAuthProvider');
  }
  return ctx;
}
